/**
 * Admin FAQ Management Page
 * - Manage FAQ categories
 * - Manage FAQ items (create, edit, publish/unpublish)
 * - View and respond to user-submitted questions
 * - Convert user questions to public FAQs
 * - AI-powered response generation
 * - Email responses to users
 * - Similarity detection for FAQ suggestions
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, Plus, Edit2, Trash2, Eye, EyeOff, Send,
  MessageSquare, ChevronDown, ChevronRight, Check, X, Clock,
  Filter, Search, AlertTriangle, CheckCircle, Loader2, Save,
  Shield, ArrowLeft, Sparkles, Mail, Users, RefreshCw, Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

export default function AdminFAQ() {
  const navigate = useNavigate();
  // Tabs
  const [activeTab, setActiveTab] = useState('items'); // items, questions, categories
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  // FAQ Items state
  const [faqItems, setFaqItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // User Questions state
  const [userQuestions, setUserQuestions] = useState([]);
  const [questionStats, setQuestionStats] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [questionFilter, setQuestionFilter] = useState(null); // null = all, pending, answered, published, rejected
  
  // Dialogs
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  
  // Form state
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category_slug: '',
    question: '',
    answer: '',
    order: 99,
    is_published: true
  });
  const [saving, setSaving] = useState(false);
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    order: 99,
    icon: '❓'
  });
  
  const [respondingQuestion, setRespondingQuestion] = useState(null);
  const [responseForm, setResponseForm] = useState({
    response: '',
    publish_as_faq: false
  });
  
  // AI Features state
  const [generatingAI, setGeneratingAI] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [checkingSimilarity, setCheckingSimilarity] = useState(false);
  const [similarityGroups, setSimilarityGroups] = useState([]);
  const [showSimilarityDialog, setShowSimilarityDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [faqFromGroupForm, setFaqFromGroupForm] = useState({
    question: '',
    answer: ''
  });
  
  // Expanded items for accordion view
  const [expandedItems, setExpandedItems] = useState({});
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const res = await adminAPI.getFAQCategories();
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Error al cargar categorías');
    } finally {
      setLoadingCategories(false);
    }
  }, []);
  
  // Fetch FAQ items
  const fetchItems = useCallback(async () => {
    try {
      setLoadingItems(true);
      const res = await adminAPI.getFAQItems(selectedCategory);
      setFaqItems(res.data.items || []);
    } catch (err) {
      console.error('Error fetching FAQ items:', err);
      toast.error('Error al cargar preguntas frecuentes');
    } finally {
      setLoadingItems(false);
    }
  }, [selectedCategory]);
  
  // Fetch user questions
  const fetchQuestions = useCallback(async () => {
    try {
      setLoadingQuestions(true);
      const res = await adminAPI.getUserQuestions(questionFilter);
      setUserQuestions(res.data.questions || []);
      setQuestionStats(res.data.stats || {});
    } catch (err) {
      console.error('Error fetching questions:', err);
      toast.error('Error al cargar preguntas de usuarios');
    } finally {
      setLoadingQuestions(false);
    }
  }, [questionFilter]);
  
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);
  
  useEffect(() => {
    if (activeTab === 'questions') {
      fetchQuestions();
    }
  }, [activeTab, fetchQuestions]);
  
  // Create/Update FAQ item
  const handleSaveItem = async () => {
    if (!itemForm.question.trim() || !itemForm.answer.trim()) {
      toast.error('La pregunta y respuesta son obligatorias');
      return;
    }
    
    setSaving(true);
    try {
      if (editingItem) {
        await adminAPI.updateFAQItem(editingItem.id, itemForm);
        toast.success('FAQ actualizado');
      } else {
        await adminAPI.createFAQItem(itemForm);
        toast.success('FAQ creado');
      }
      setShowItemDialog(false);
      setEditingItem(null);
      setItemForm({ category_slug: '', question: '', answer: '', order: 99, is_published: true });
      fetchItems();
    } catch (err) {
      console.error('Error saving FAQ item:', err);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle publish status
  const handleTogglePublish = async (item) => {
    try {
      await adminAPI.updateFAQItem(item.id, { is_published: !item.is_published });
      toast.success(item.is_published ? 'FAQ ocultado' : 'FAQ publicado');
      fetchItems();
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };
  
  // Delete FAQ item
  const handleDeleteItem = async (itemId) => {
    if (!confirm('¿Estás seguro de eliminar esta pregunta?')) return;
    
    try {
      await adminAPI.deleteFAQItem(itemId);
      toast.success('FAQ eliminado');
      fetchItems();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };
  
  // Create category
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim() || !categoryForm.slug.trim()) {
      toast.error('Nombre y slug son obligatorios');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.createFAQCategory(categoryForm);
      toast.success('Categoría creada');
      setShowCategoryDialog(false);
      setCategoryForm({ name: '', slug: '', order: 99, icon: '❓' });
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear categoría');
    } finally {
      setSaving(false);
    }
  };
  
  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('¿Estás seguro de eliminar esta categoría? Las preguntas NO se eliminarán.')) return;
    
    try {
      await adminAPI.deleteFAQCategory(categoryId);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };
  
  // Respond to user question
  const handleRespondToQuestion = async () => {
    if (!responseForm.response.trim()) {
      toast.error('La respuesta es obligatoria');
      return;
    }
    
    setSaving(true);
    try {
      await adminAPI.respondToQuestion(respondingQuestion.id, responseForm);
      toast.success(responseForm.publish_as_faq 
        ? 'Pregunta respondida y publicada como FAQ' 
        : 'Pregunta respondida'
      );
      setShowResponseDialog(false);
      setRespondingQuestion(null);
      setResponseForm({ response: '', publish_as_faq: false });
      fetchQuestions();
      if (responseForm.publish_as_faq) fetchItems();
    } catch (err) {
      toast.error('Error al responder');
    } finally {
      setSaving(false);
    }
  };
  
  // Update question status
  const handleUpdateQuestionStatus = async (questionId, status) => {
    try {
      await adminAPI.updateQuestionStatus(questionId, status);
      toast.success('Estado actualizado');
      fetchQuestions();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };
  
  // AI: Generate response suggestion
  const handleGenerateAIResponse = async (question) => {
    setGeneratingAI(true);
    try {
      const res = await adminAPI.generateAIResponse(question.id);
      if (res.data.success) {
        setResponseForm(prev => ({ ...prev, response: res.data.response }));
        toast.success('Respuesta generada por IA');
      } else {
        toast.error(res.data.error || 'Error al generar respuesta');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      toast.error('Error al generar respuesta con IA');
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // AI: Send email response
  const handleSendEmailResponse = async () => {
    if (!responseForm.response.trim()) {
      toast.error('Escribe una respuesta primero');
      return;
    }
    
    setSendingEmail(true);
    try {
      const res = await adminAPI.sendEmailResponse(respondingQuestion.id, {
        response: responseForm.response
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowResponseDialog(false);
        setRespondingQuestion(null);
        setResponseForm({ response: '', publish_as_faq: false });
        fetchQuestions();
      }
    } catch (err) {
      console.error('Email send error:', err);
      toast.error(err.response?.data?.detail || 'Error al enviar email');
    } finally {
      setSendingEmail(false);
    }
  };
  
  // AI: Check similarity with other questions
  const handleCheckSimilarity = async (question) => {
    setCheckingSimilarity(true);
    try {
      const res = await adminAPI.checkQuestionSimilarity(question.id);
      if (res.data.success) {
        if (res.data.should_suggest_faq) {
          toast.success(`¡Encontradas ${res.data.similar_count + 1} preguntas similares! Considera crear un FAQ.`);
        } else if (res.data.similar_count > 0) {
          toast.info(`Encontrada ${res.data.similar_count} pregunta similar`);
        } else {
          toast.info('No se encontraron preguntas similares');
        }
        fetchQuestions();
      }
    } catch (err) {
      console.error('Similarity check error:', err);
      toast.error('Error al verificar similitud');
    } finally {
      setCheckingSimilarity(false);
    }
  };
  
  // AI: Fetch similarity groups
  const fetchSimilarityGroups = async () => {
    try {
      const res = await adminAPI.getSimilarityGroups();
      setSimilarityGroups(res.data.groups || []);
      if (res.data.total_groups > 0) {
        toast.info(`${res.data.total_groups} grupos de preguntas similares encontrados`);
      }
    } catch (err) {
      console.error('Error fetching similarity groups:', err);
    }
  };
  
  // AI: Create FAQ from similarity group
  const handleCreateFAQFromGroup = async () => {
    if (!faqFromGroupForm.question.trim() || !faqFromGroupForm.answer.trim()) {
      toast.error('La pregunta y respuesta son obligatorias');
      return;
    }
    
    setSaving(true);
    try {
      const res = await adminAPI.createFAQFromGroup({
        question_ids: selectedGroup.question_ids,
        suggested_faq_question: faqFromGroupForm.question,
        suggested_faq_answer: faqFromGroupForm.answer
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowSimilarityDialog(false);
        setSelectedGroup(null);
        setFaqFromGroupForm({ question: '', answer: '' });
        fetchSimilarityGroups();
        fetchQuestions();
        fetchItems();
      }
    } catch (err) {
      console.error('Create FAQ from group error:', err);
      toast.error('Error al crear FAQ');
    } finally {
      setSaving(false);
    }
  };
  
  // Get category name by slug
  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? `${cat.icon} ${cat.name}` : slug;
  };
  
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Gestión de FAQ</h1>
              <p className="text-xs text-muted-foreground">Administrador</p>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-4">
          
          {/* Quick Stats */}
          <div className="flex gap-4 text-sm mb-6">
            <div className="px-3 py-1.5 bg-yellow-500/20 text-yellow-500 rounded-full">
              {questionStats.pending || 0} pendientes
            </div>
            <div className="px-3 py-1.5 bg-green-500/20 text-green-500 rounded-full">
              {faqItems.filter(f => f.is_published).length} publicadas
            </div>
          </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('items')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'items' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <HelpCircle className="w-4 h-4 inline mr-2" />
            FAQs ({faqItems.length})
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'questions' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Preguntas de usuarios
            {(questionStats.pending || 0) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-yellow-500 text-black rounded-full">
                {questionStats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('similarity');
              fetchSimilarityGroups();
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'similarity' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            Preguntas similares
            {similarityGroups.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                {similarityGroups.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'categories' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Filter className="w-4 h-4 inline mr-2" />
            Categorías ({categories.length})
          </button>
        </div>
        
        {/* FAQ Items Tab */}
        {activeTab === 'items' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Filters and Actions */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value || null)}
                className="px-3 py-2 rounded-md bg-secondary border border-border text-sm"
              >
                <option value="">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
              
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setItemForm({ 
                    category_slug: categories[0]?.slug || 'otros', 
                    question: '', 
                    answer: '', 
                    order: 99, 
                    is_published: true 
                  });
                  setShowItemDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo FAQ
              </Button>
            </div>
            
            {/* FAQ List */}
            {loadingItems ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : (
              <div className="space-y-2">
                {faqItems.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay preguntas frecuentes</p>
                      <p className="text-sm">Crea la primera usando el botón &quot;Nuevo FAQ&quot;</p>
                    </CardContent>
                  </Card>
                ) : (
                  faqItems.map((item, idx) => (
                    <Card key={item.id} className={!item.is_published ? 'opacity-60' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 bg-secondary rounded-full">
                                {getCategoryName(item.category_slug)}
                              </span>
                              {!item.is_published && (
                                <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-500 rounded-full">
                                  No publicado
                                </span>
                              )}
                              {item.source === 'user_question' && (
                                <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-500 rounded-full">
                                  De usuario
                                </span>
                              )}
                            </div>
                            
                            <button
                              onClick={() => setExpandedItems(prev => ({
                                ...prev,
                                [item.id]: !prev[item.id]
                              }))}
                              className="w-full text-left"
                            >
                              <h3 className="font-medium flex items-center gap-2">
                                {expandedItems[item.id] ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                )}
                                {item.question}
                              </h3>
                            </button>
                            
                            <AnimatePresence>
                              {expandedItems[item.id] && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="mt-2 pl-6"
                                >
                                  <p className="text-sm text-muted-foreground bg-secondary/50 p-3 rounded-lg whitespace-pre-wrap">
                                    {item.answer}
                                  </p>
                                  <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                                    <span>Orden: {item.order}</span>
                                    <span>Vistas: {item.views || 0}</span>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleTogglePublish(item)}
                              title={item.is_published ? 'Ocultar' : 'Publicar'}
                            >
                              {item.is_published ? (
                                <Eye className="w-4 h-4 text-green-500" />
                              ) : (
                                <EyeOff className="w-4 h-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingItem(item);
                                setItemForm({
                                  category_slug: item.category_slug,
                                  question: item.question,
                                  answer: item.answer,
                                  order: item.order,
                                  is_published: item.is_published
                                });
                                setShowItemDialog(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </motion.div>
        )}
        
        {/* User Questions Tab */}
        {activeTab === 'questions' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card 
                className={`cursor-pointer transition-all ${!questionFilter ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setQuestionFilter(null)}
              >
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold">{questionStats.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all ${questionFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
                onClick={() => setQuestionFilter('pending')}
              >
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-500">{questionStats.pending || 0}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all ${questionFilter === 'answered' ? 'ring-2 ring-green-500' : ''}`}
                onClick={() => setQuestionFilter('answered')}
              >
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-500">{questionStats.answered || 0}</p>
                  <p className="text-xs text-muted-foreground">Respondidas</p>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer transition-all ${questionFilter === 'published' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setQuestionFilter('published')}
              >
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-500">{questionStats.published || 0}</p>
                  <p className="text-xs text-muted-foreground">Publicadas como FAQ</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Questions List */}
            {loadingQuestions ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {userQuestions.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay preguntas de usuarios</p>
                    </CardContent>
                  </Card>
                ) : (
                  userQuestions.map((q) => (
                    <Card key={q.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                q.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                q.status === 'answered' ? 'bg-green-500/20 text-green-500' :
                                q.status === 'published' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-red-500/20 text-red-500'
                              }`}>
                                {q.status === 'pending' ? 'Pendiente' :
                                 q.status === 'answered' ? 'Respondida' :
                                 q.status === 'published' ? 'Publicada' :
                                 'Rechazada'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {getCategoryName(q.category_slug)}
                              </span>
                              {q.email_sent && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                                  <Mail className="w-3 h-3 inline mr-1" />
                                  Email enviado
                                </span>
                              )}
                              {q.similar_question_ids?.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-500">
                                  <Users className="w-3 h-3 inline mr-1" />
                                  {q.similar_question_ids.length + 1} similares
                                </span>
                              )}
                            </div>
                            
                            <p className="font-medium">{q.question}</p>
                            
                            {/* AI Generated Response Preview */}
                            {q.ai_suggested_response && !q.response && (
                              <div className="mt-2 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                                <p className="text-xs text-purple-400 mb-1 flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Respuesta sugerida por IA:
                                </p>
                                <p className="text-sm text-muted-foreground">{q.ai_suggested_response}</p>
                              </div>
                            )}
                            
                            {q.response && (
                              <div className="mt-2 p-3 bg-secondary/50 rounded-lg">
                                <p className="text-xs text-primary mb-1">Respuesta:</p>
                                <p className="text-sm text-muted-foreground">{q.response}</p>
                                {q.responded_by_email && (
                                  <p className="text-xs text-muted-foreground mt-2">
                                    — {q.responded_by_email}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                              <span>De: {q.user_email || 'Anónimo'}</span>
                              <span>
                                {new Date(q.created_at).toLocaleDateString('es-MX', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {q.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setRespondingQuestion(q);
                                    setResponseForm({ 
                                      response: q.ai_suggested_response || '', 
                                      publish_as_faq: false 
                                    });
                                    setShowResponseDialog(true);
                                  }}
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Responder
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="gap-1"
                                  onClick={() => handleCheckSimilarity(q)}
                                  disabled={checkingSimilarity}
                                >
                                  {checkingSimilarity ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Users className="w-4 h-4" />
                                  )}
                                  Similares
                                </Button>
                              </>
                            )}
                            {q.status === 'answered' && !q.email_sent && q.user_email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setRespondingQuestion(q);
                                  setResponseForm({ response: q.response, publish_as_faq: false });
                                  setShowResponseDialog(true);
                                }}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Enviar email
                              </Button>
                            )}
                            {q.status === 'answered' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateQuestionStatus(q.id, 'published')}
                              >
                                Publicar como FAQ
                              </Button>
                            )}
                            {q.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => handleUpdateQuestionStatus(q.id, 'rejected')}
                              >
                                Rechazar
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </motion.div>
        )}
        
        {/* Similarity Groups Tab */}
        {activeTab === 'similarity' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">Grupos de preguntas similares</h3>
                  <p className="text-sm text-muted-foreground">
                    Cuando 3+ usuarios hacen preguntas similares, considera crear un FAQ
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={fetchSimilarityGroups}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualizar
                </Button>
              </div>
              
              {similarityGroups.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay grupos de preguntas similares</p>
                    <p className="text-sm">Analiza las preguntas pendientes usando el botón &quot;Similares&quot;</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {similarityGroups.map((group, idx) => (
                    <Card key={idx} className="border-purple-500/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                                {group.count} preguntas similares
                              </span>
                              {group.should_create_faq && (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                                  ✓ Listo para FAQ
                                </span>
                              )}
                            </div>
                            
                            {group.summary && (
                              <p className="text-sm text-muted-foreground mb-3">
                                <strong>Tema:</strong> {group.summary}
                              </p>
                            )}
                            
                            <div className="space-y-2">
                              {group.questions?.map((q, qIdx) => (
                                <div key={qIdx} className="p-2 bg-secondary/50 rounded text-sm">
                                  <p className="font-medium">{q.question}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {q.user_email || 'Anónimo'} - {new Date(q.created_at).toLocaleDateString('es-MX')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <Button
                            onClick={() => {
                              setSelectedGroup(group);
                              setFaqFromGroupForm({
                                question: group.questions?.[0]?.question || '',
                                answer: ''
                              });
                              setShowSimilarityDialog(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Crear FAQ
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
        
        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => {
                  setCategoryForm({ name: '', slug: '', order: 99, icon: '❓' });
                  setShowCategoryDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Categoría
              </Button>
            </div>
            
            {loadingCategories ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((cat) => (
                  <Card key={cat.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{cat.icon}</span>
                          <div>
                            <h3 className="font-medium">{cat.name}</h3>
                            <p className="text-xs text-muted-foreground">/{cat.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            Orden: {cat.order}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCategory(cat.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {faqItems.filter(f => f.category_slug === cat.slug).length} preguntas
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
      
      {/* Create/Edit FAQ Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Editar FAQ' : 'Nuevo FAQ'}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? 'Modifica la pregunta y respuesta' : 'Crea una nueva pregunta frecuente'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <select
                  value={itemForm.category_slug}
                  onChange={(e) => setItemForm({ ...itemForm, category_slug: e.target.value })}
                  className="w-full p-2 rounded-md bg-secondary border border-border"
                >
                  {categories.map(cat => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Orden</Label>
                <Input
                  type="number"
                  value={itemForm.order}
                  onChange={(e) => setItemForm({ ...itemForm, order: parseInt(e.target.value) || 99 })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Pregunta</Label>
              <Input
                value={itemForm.question}
                onChange={(e) => setItemForm({ ...itemForm, question: e.target.value })}
                placeholder="¿Cómo puedo...?"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Respuesta</Label>
              <textarea
                value={itemForm.answer}
                onChange={(e) => setItemForm({ ...itemForm, answer: e.target.value })}
                placeholder="La respuesta es..."
                rows={5}
                className="w-full p-3 rounded-md bg-secondary border border-border resize-none"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={itemForm.is_published}
                onChange={(e) => setItemForm({ ...itemForm, is_published: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_published">Publicar inmediatamente</Label>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveItem} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Guardar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Categoría</DialogTitle>
            <DialogDescription>
              Crea una nueva categoría para organizar las preguntas frecuentes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Pagos y Suscripciones"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Slug (URL)</Label>
                <Input
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ 
                    ...categoryForm, 
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') 
                  })}
                  placeholder="pagos"
                />
              </div>
              <div className="space-y-2">
                <Label>Icono (emoji)</Label>
                <Input
                  value={categoryForm.icon}
                  onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  placeholder="💳"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Orden</Label>
              <Input
                type="number"
                value={categoryForm.order}
                onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 99 })}
              />
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveCategory} disabled={saving}>
              {saving ? 'Guardando...' : 'Crear Categoría'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Response Dialog with AI Features */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Responder pregunta</DialogTitle>
            <DialogDescription>
              Genera una respuesta con IA, edítala y envíala por email al usuario
            </DialogDescription>
          </DialogHeader>
          
          {respondingQuestion && (
            <div className="space-y-4 mt-4">
              <div className="bg-secondary/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Pregunta de {respondingQuestion.user_email || 'Anónimo'}:</p>
                <p className="font-medium">{respondingQuestion.question}</p>
              </div>
              
              {/* AI Generate Button */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="gap-2 flex-1"
                  onClick={() => handleGenerateAIResponse(respondingQuestion)}
                  disabled={generatingAI}
                >
                  {generatingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando con IA...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generar respuesta con IA
                    </>
                  )}
                </Button>
              </div>
              
              <div className="space-y-2">
                <Label>Tu respuesta (puedes editar la sugerencia de IA)</Label>
                <textarea
                  value={responseForm.response}
                  onChange={(e) => setResponseForm({ ...responseForm, response: e.target.value })}
                  placeholder="Escribe tu respuesta o genera una con IA..."
                  rows={6}
                  className="w-full p-3 rounded-md bg-secondary border border-border resize-none"
                />
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <input
                  type="checkbox"
                  id="publish_as_faq"
                  checked={responseForm.publish_as_faq}
                  onChange={(e) => setResponseForm({ ...responseForm, publish_as_faq: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="publish_as_faq" className="text-sm">
                  Publicar también como FAQ pública (otros usuarios podrán ver esta pregunta y respuesta)
                </Label>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>
              Cancelar
            </Button>
            {respondingQuestion?.user_email && (
              <Button 
                variant="outline" 
                onClick={handleSendEmailResponse} 
                disabled={sendingEmail || !responseForm.response.trim()}
                className="gap-2"
              >
                {sendingEmail ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                ) : (
                  <><Mail className="w-4 h-4" /> Enviar por email</>
                )}
              </Button>
            )}
            <Button onClick={handleRespondToQuestion} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Guardar respuesta</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create FAQ from Similarity Group Dialog */}
      <Dialog open={showSimilarityDialog} onOpenChange={setShowSimilarityDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-500" />
              Crear FAQ desde preguntas similares
            </DialogTitle>
            <DialogDescription>
              {selectedGroup?.count} usuarios han hecho preguntas similares. Crea una FAQ que las responda a todas.
            </DialogDescription>
          </DialogHeader>
          
          {selectedGroup && (
            <div className="space-y-4 mt-4">
              {/* Preview of similar questions */}
              <div className="bg-secondary/50 p-3 rounded-lg max-h-40 overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-2">Preguntas de los usuarios:</p>
                {selectedGroup.questions?.map((q, idx) => (
                  <p key={idx} className="text-sm mb-1">• {q.question}</p>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label>Pregunta para el FAQ (puedes editarla)</Label>
                <Input
                  value={faqFromGroupForm.question}
                  onChange={(e) => setFaqFromGroupForm(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="¿Cómo puedo...?"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Respuesta</Label>
                <textarea
                  value={faqFromGroupForm.answer}
                  onChange={(e) => setFaqFromGroupForm(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Escribe la respuesta que servirá para todos los usuarios con esta duda..."
                  rows={5}
                  className="w-full p-3 rounded-md bg-secondary border border-border resize-none"
                />
              </div>
              
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <p className="text-sm text-green-400">
                  ✓ Al crear este FAQ, las {selectedGroup.count} preguntas se marcarán como &quot;Publicadas&quot;
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowSimilarityDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateFAQFromGroup} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Crear FAQ</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
